import React, { useEffect, useState } from "react";
import { Wallet, Landmark, ArrowDownLeft, Pencil, ShieldAlert, Star, CalendarCheck, PiggyBank, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { inr, stars, catLabel } from "@/lib/format";
import { EditProfileModal, WithdrawRequestModal } from "@/components/MemberModals";
import KycModal from "@/components/KycModal";

export default function MemberHome({ setTab }) {
  const { member, refresh } = useAuth();
  const [me, setMe] = useState(member);
  const [txns, setTxns] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [kycOpen, setKycOpen] = useState(false);

  const load = async () => {
    const [m, t] = await Promise.all([api.get("/members"), api.get("/transactions/me")]);
    setMe(m.data.find((x) => x.id === member.id) || member);
    setTxns(t.data.slice(0, 4));
  };
  useEffect(() => { load(); }, []);

  const cat = catLabel(me.category);
  const loanProgress = me.loan_taken > 0 ? Math.round(((me.loan_taken - me.loan_remaining) / me.loan_taken) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Profile card */}
      <div data-testid="member-profile-card" className="relative overflow-hidden rounded-2xl bg-primary text-white p-5 shadow-lg">
        <div className="absolute -top-10 -right-8 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="relative flex items-center gap-3">
          <Avatar name={me.name} size={56} />
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-xl truncate" data-testid="own-name">{me.name}</p>
            <p className="text-white/70 text-xs font-mono">{me.member_code}</p>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cat.cls}`} data-testid="own-category">
            {cat.hi}
          </span>
        </div>
        <div className="relative grid grid-cols-3 gap-2 mt-4">
          <div className="rounded-xl bg-white/10 p-2.5">
            <p className="text-[10px] text-white/70 uppercase">Savings</p>
            <p className="font-mono font-bold text-sm" data-testid="own-savings">{inr(me.savings)}</p>
          </div>
          <div className="rounded-xl bg-white/10 p-2.5">
            <p className="text-[10px] text-white/70 uppercase">Balance</p>
            <p className="font-mono font-bold text-sm" data-testid="own-balance">{inr(me.balance)}</p>
          </div>
          <div className="rounded-xl bg-white/10 p-2.5">
            <p className="text-[10px] text-white/70 uppercase">योगदान</p>
            <p className="font-mono font-bold text-sm">{inr(me.contribution)}</p>
          </div>
        </div>
        <div className="relative flex items-center justify-between mt-3 text-xs text-white/85">
          <span className="flex items-center gap-1" data-testid="own-credit"><Star className="h-3.5 w-3.5 text-amber-300" /> {stars(me.credit_score)}</span>
          <span className="flex items-center gap-1" data-testid="own-attendance"><CalendarCheck className="h-3.5 w-3.5" /> {me.attendance_present}/{me.attendance_total}</span>
        </div>
      </div>

      {/* KYC banner */}
      {me.kyc_status !== "verified" && (
        <button
          data-testid="kyc-banner"
          onClick={() => setKycOpen(true)}
          className="w-full flex items-center gap-3 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-left"
        >
          <ShieldAlert className="h-7 w-7 text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-800 font-display">आधार KYC बाकी है</p>
            <p className="text-xs text-amber-700">अभी पूरा करें — तेज़ और सुरक्षित</p>
          </div>
          <span className="text-amber-700 font-semibold text-sm">पूरा करें ›</span>
        </button>
      )}

      {/* Loan summary */}
      {me.loan_taken > 0 && (
        <div className="rounded-2xl bg-white p-4 shadow-sm border border-slate-100" data-testid="home-loan-card">
          <div className="flex items-center justify-between mb-2">
            <p className="font-display font-semibold flex items-center gap-1.5"><Landmark className="h-4 w-4 text-primary" /> मेरा ऋण / My Loan</p>
            {me.is_npa && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">NPA</span>}
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">बाकी / Remaining</span>
            <span className="font-mono font-semibold">{inr(me.loan_remaining)}</span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${loanProgress}%` }} />
          </div>
          <button onClick={() => setTab("loans")} className="mt-3 text-sm text-primary font-semibold" data-testid="goto-loans-btn">
            EMI देखें और भुगतान करें ›
          </button>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { icon: Wallet, hi: "जमा", t: () => setTab("jama"), tid: "qa-jama" },
          { icon: Landmark, hi: "ऋण", t: () => setTab("loans"), tid: "qa-loan" },
          { icon: ArrowDownLeft, hi: "निकासी", t: () => setWithdrawOpen(true), tid: "qa-withdraw" },
          { icon: Pencil, hi: "एडिट", t: () => setEditOpen(true), tid: "qa-edit" },
        ].map((a) => (
          <button key={a.hi} data-testid={a.tid} onClick={a.t} className="flex flex-col items-center gap-1.5 rounded-2xl bg-white p-3 shadow-sm border border-slate-100 hover:border-primary transition-colors">
            <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center">
              <a.icon className="h-5 w-5 text-primary" />
            </div>
            <span className="text-xs font-semibold font-display">{a.hi}</span>
          </button>
        ))}
      </div>

      {/* Recent history */}
      <div className="rounded-2xl bg-white p-4 shadow-sm border border-slate-100">
        <p className="font-display font-semibold mb-3">हाल का लेन-देन / Recent</p>
        {txns.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-3">कोई लेन-देन नहीं</p>
        ) : (
          <div className="space-y-2">
            {txns.map((t) => {
              const j = t.type === "jama";
              return (
                <div key={t.id} className="flex items-center justify-between" data-testid={`home-txn-${t.id}`}>
                  <div className="flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center ${j ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-600"}`}>
                      {j ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{j ? "जमा" : "निकासी"}</p>
                      <p className="text-[11px] text-muted-foreground">{t.note}</p>
                    </div>
                  </div>
                  <span className={`font-mono text-sm font-semibold ${j ? "text-emerald-600" : "text-red-600"}`}>{j ? "+" : "-"}{inr(t.amount)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <EditProfileModal open={editOpen} onOpenChange={setEditOpen} me={me} onDone={async () => { await refresh(); await load(); }} />
      <WithdrawRequestModal open={withdrawOpen} onOpenChange={setWithdrawOpen} maxBalance={me.balance} />
      <KycModal open={kycOpen} onOpenChange={setKycOpen} onDone={async () => { await refresh(); await load(); }} />
    </div>
  );
}
